// src/components/access/AccessChangedNotice.tsx
//
// "An administrator changed your access" — live, in the member's open tab.
//
// ============================================================
// WHY THIS EXISTS
// ============================================================
// Central clears the member's cached authorization context the moment an
// admin saves, and that fixes their NEXT request. It does nothing for the
// tab they already have open: it booted with a capability set and keeps
// rendering it until something makes it reload. So an admin would switch a
// menu off, the member would keep seeing it, and both would reasonably
// conclude the change had not worked.
//
// CRM's own websocket already reaches that member (it authenticates from the
// RP session cookie, so it works where Central's browser SSE stream does
// not — that stream needs a Central bearer token the RP browser never has).
// The same internal call Central makes to clear the cache now also pushes
// `access.changed` to that one user, and this listens for it.
//
// ============================================================
// THE EVENT IS A SIGNAL, NOT DATA
// ============================================================
// The payload carries no capabilities and none are read from it. This
// refetches the authoritative context and lets that replace what is in the
// store. A push that described the new access would be a second source of
// truth for the one thing that must have exactly one — and a stale or
// tampered one would WIDEN access rather than narrow it.
//
// Telling the user is the other half. Their sidebar is about to change
// underneath them; a rail that silently loses two items reads as a bug.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { useRealtimeEvent } from '../../realtime/useRealtime';
import { RealtimeEventType } from '../../realtime/events';
import { fetchAccessVersion, fetchRpContext } from '../../api/rpContextApi';
import { rpContextToBootReady } from '../auth/rpBootAdapter';
import { updateBootContext, selectBootStatus } from '../../store/slices/bootContextSlice';
import { selectCentralAccessToken } from '../../store/slices/centralAuthSlice';
import { refetchSwitchContext } from '../../api/centralSuiteApi';

/**
 * Per-user events are addressed by `emitToUser` and carry no topic, so they
 * are subscribed on the wildcard rather than a `workspace:`/`conversation:`
 * channel.
 */
const ANY_TOPIC = '*';

/**
 * How long a member may keep access an administrator has removed.
 *
 * The product requirement is five seconds. Lowering the shared switch-context
 * TTL to match would have imposed the same cost on every request in the suite;
 * this pays it only on a probe built to be cheap.
 */
const POLL_INTERVAL_MS = 5000;

export function AccessChangedNotice() {
  const dispatch = useAppDispatch();
  const bootStatus = useAppSelector(selectBootStatus);
  const accessToken = useAppSelector(selectCentralAccessToken);
  const [visible, setVisible] = useState(false);

  /**
   * Reload the authoritative context.
   *
   * Two boot paths, and the RP one is the reason this cannot simply call
   * `refetchSwitchContext`: that needs a Central bearer token, and an RP
   * browser has none. `/auth/context` is cookie-authenticated and is the
   * same endpoint RpBootGate boots from, so this is a re-run of the boot
   * fetch rather than a second way of learning the same thing.
   *
   * `updateBootContext`, never `setBootReady`/`setBootLoading` — flipping
   * the boot lifecycle would unmount the app under someone mid-task.
   */
  const refresh = useCallback(async () => {
    try {
      if (accessToken) {
        await refetchSwitchContext(accessToken, dispatch);
        return;
      }
      const result = await fetchRpContext({ fresh: true });
      if (!result.ok) return;
      const payload = rpContextToBootReady(result.context);
      // Fail closed: an unusable context leaves the previous one in place
      // rather than hydrating a half-empty capability set, which would look
      // like a much bigger access removal than actually happened.
      if (payload) dispatch(updateBootContext(payload));
    } catch {
      // Never throws. The cache is already cleared server-side, so the next
      // navigation picks up the change regardless; the banner has told them
      // to expect it.
    }
  }, [accessToken, dispatch]);

  const onAccessChanged = useCallback(() => {
    setVisible(true);
    void refresh();
  }, [refresh]);

  useRealtimeEvent(ANY_TOPIC, RealtimeEventType.ACCESS_CHANGED, onAccessChanged);

  /**
   * SECOND PATH: poll a cheap version probe every 5 seconds.
   *
   * The websocket push above is instant and remains the primary path, but it
   * only fires when the write and the reader are on the SAME deployment —
   * Central clears caches by calling one configured CRM URL and one Redis, and
   * a second Central against the same database can reach neither. That is a
   * normal state while developing, and "correct only if you edited from the
   * right machine" is not a property worth shipping.
   *
   * So this reads the one row that actually changed. It costs one indexed
   * lookup per tick — switch-context is deliberately NOT polled.
   *
   * The two paths are complementary, not redundant: the push wins on latency,
   * the poll wins on reach, and both end at the same repair.
   */
  const lastVersion = useRef<string | null>(null);

  useEffect(() => {
    if (bootStatus !== 'ready') return;
    let cancelled = false;

    const tick = async () => {
      // Don't poll a tab nobody is looking at. It resumes on focus, and the
      // first tick after that catches anything missed while hidden.
      if (typeof document !== 'undefined' && document.hidden) return;

      const version = await fetchAccessVersion();
      if (cancelled || version === null) return;

      // First reading establishes the baseline. Treating it as a change would
      // fire the banner on every page load.
      if (lastVersion.current === null) {
        lastVersion.current = version;
        return;
      }
      if (version === lastVersion.current) return;

      lastVersion.current = version;
      setVisible(true);
      void refresh();
    };

    void tick();
    const id = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [bootStatus, refresh]);

  // A change that arrives mid-boot has nowhere to land — the store is about
  // to be hydrated from scratch anyway, which already has the new access.
  useEffect(() => {
    if (bootStatus !== 'ready') setVisible(false);
  }, [bootStatus]);

  if (!visible) return null;

  return (
    <div
      data-testid="access-changed-notice"
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-3 border-b border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-900"
    >
      <span>
        <strong className="font-semibold">Your access was updated.</strong>{' '}
        An administrator changed what you can use here, and this page now
        reflects it.
      </span>
      <button
        type="button"
        data-testid="access-changed-dismiss"
        onClick={() => setVisible(false)}
        className="shrink-0 rounded-lg border border-indigo-300 bg-white px-2.5 py-1 text-xs font-medium text-indigo-800 shadow-sm transition hover:bg-indigo-100"
      >
        Dismiss
      </button>
    </div>
  );
}

export default AccessChangedNotice;
